/** The Watchtower connect ceremony — a one-time, user-driven authorization that
 *  lets the trading backend produce telemetry to Penseur Watchtower ON BEHALF OF
 *  the signed-in user.
 *
 *  Why a second OAuth round-trip at all: every token trading presents to
 *  Watchtower is an RFC 8693 DELEGATED token (`sub` = the user, `act.sub` = the
 *  producer agent), and Watchtower partitions usage rows on `sub` verbatim. The
 *  delegation chain therefore has to start from a refresh token minted for the
 *  USER against the WATCHTOWER platform/tenant — which only the user can
 *  authorize. The console runs a normal PKCE authorize against
 *  `penseur-watchtower` (cookie-silent: the user already has a Guardius session
 *  from console sign-in, so no second wallet signature is needed), and hands the
 *  resulting CODE to the trading backend, which exchanges it and custodies the
 *  refresh chain in SSM (`POST /watchtower/connect`, the watchtower-api Lambda).
 *  The console never sees the Watchtower tokens — only the code.
 *
 *  After connect, the backend returns the hosted CONSENT picker URL; the user is
 *  sent there to grant the producer agent delegation rights, and lands back on
 *  the Usage tab.
 *
 *  No React and no storage abstraction here — sessionStorage + fetch only, so
 *  the whole ceremony is unit-testable. */

import { buildAuthorizeUrl, generatePkce, randomState } from './oauth';

/** The Watchtower platform this ceremony authorizes against — deliberately NOT
 *  `penseur-trading` (oauth.PLATFORM_ID). Provisioned value, verbatim. */
export const WATCHTOWER_PLATFORM_ID = 'penseur-watchtower';

/** The Watchtower tenant that homes the producer agent and the usage rows.
 *  Passed explicitly (not left to membership resolution) so the ceremony can
 *  never resolve to some other Watchtower tenant. Provisioned value, verbatim. */
export const WATCHTOWER_TENANT_ID = 'tenant-b7fc514e-4c33-4e4f-845a-e267474c49e6';

/** One-shot sessionStorage stash for the ceremony's PKCE material. A SEPARATE
 *  key from the sign-in stash (`console.oauth.pending`): the boot sequence has
 *  to tell a Watchtower callback from a sign-in callback BEFORE it touches — and
 *  consumes — the sign-in stash. */
export const WT_PENDING_KEY = 'console.watchtower.pending';

/** The console path the ceremony returns to (the SPA lives at /dashboard/). */
const CALLBACK_PATH = '/dashboard/';

/** Query param carrying the post-consent landing tab. The SPA routes on the URL
 *  HASH (App.tsx tabFromHash), but a hash CANNOT appear here: Guardius rejects
 *  any `redirect_uri` containing a fragment outright ("Redirect URI must not
 *  contain a fragment (#)"), per RFC 6749 §3.1.2. So the return hop travels as a
 *  query param and `applyConsentReturn()` converts it to a hash at boot. */
export const CONSENT_RETURN_PARAM = 'wt';

/** Where the user lands after granting consent: back on the Usage tab. */
const CONSENT_RETURN_PATH = `/dashboard/?${CONSENT_RETURN_PARAM}=usage`;

interface WatchtowerPending {
  verifier: string;
  state: string;
  redirectUri: string;
}

/** Read the stash without consuming it; `null` when absent or corrupt. */
function readPending(): WatchtowerPending | null {
  const raw = sessionStorage.getItem(WT_PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<WatchtowerPending>;
    if (typeof parsed?.verifier !== 'string' || typeof parsed?.state !== 'string') return null;
    if (typeof parsed?.redirectUri !== 'string') return null;
    return parsed as WatchtowerPending;
  } catch {
    return null;
  }
}

/** Discard the ceremony stash (a callback we can't complete — e.g. no session). */
export function dropWatchtowerCeremony(): void {
  sessionStorage.removeItem(WT_PENDING_KEY);
}

/** Start the ceremony: mint PKCE + state, stash them, and navigate to Guardius's
 *  hosted `/auth/authorize` scoped to the WATCHTOWER platform + tenant. The user
 *  already holds a Guardius session cookie from console sign-in, so this is
 *  normally a silent bounce straight back with `?code&state`. */
export async function beginWatchtowerCeremony(guardiusUrl: string): Promise<void> {
  const { verifier, challenge } = await generatePkce();
  const state = randomState();
  const redirectUri = window.location.origin + CALLBACK_PATH;
  const pending: WatchtowerPending = { verifier, state, redirectUri };
  sessionStorage.setItem(WT_PENDING_KEY, JSON.stringify(pending));
  const authorizeUrl = buildAuthorizeUrl(
    { guardiusUrl },
    {
      challenge,
      state,
      redirectUri,
      platformId: WATCHTOWER_PLATFORM_ID,
      tenantId: WATCHTOWER_TENANT_ID,
    },
  );
  window.location.assign(authorizeUrl);
}

/** True iff this callback belongs to the Watchtower ceremony (a stash exists and
 *  its state matches). Non-destructive — the boot sequence calls this as a
 *  discriminator before deciding which stash to consume. */
export function matchesWatchtowerCallback(state: string | null): boolean {
  if (!state) return false;
  const pending = readPending();
  return pending !== null && pending.state === state;
}

/** Finish the ceremony: hand the code + stashed verifier to the trading backend,
 *  which exchanges it against Watchtower and custodies the refresh chain. The
 *  stash is consumed FIRST (one-shot, exactly like the sign-in exchange), so a
 *  failure can't be retried against an already-spent code — the card surfaces
 *  the error and the user restarts the ceremony deliberately.
 *
 *  Throws on anything other than a 200 carrying a `consent_url`. */
export async function completeWatchtowerCallback(
  apiUrl: string,
  accessToken: string,
  code: string,
): Promise<{ consentUrl: string }> {
  const pending = readPending();
  dropWatchtowerCeremony();
  if (!pending) throw new Error('Watchtower connect could not be verified (missing request state).');

  const res = await fetch(`${apiUrl}/watchtower/connect`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({
      code,
      code_verifier: pending.verifier,
      redirect_uri: pending.redirectUri,
    }),
  });
  if (!res.ok) throw new Error(`Watchtower connect failed (${res.status}).`);
  const body = (await res.json()) as { connected?: boolean; consent_url?: string };
  if (!body.consent_url) throw new Error('Watchtower connect returned no consent URL.');
  // The consent URL is server-supplied but ends up in `location.assign` and in
  // an <a href>, so validate the scheme before either: a `javascript:` (or other
  // non-https) value would otherwise be a redirect/XSS sink.
  if (!isSafeConsentUrl(body.consent_url)) {
    throw new Error('Watchtower connect returned an unusable consent URL.');
  }
  return { consentUrl: body.consent_url };
}

/** True iff `url` parses as an absolute https URL. Everything that navigates to
 *  or renders a server-supplied consent URL gates on this. */
export function isSafeConsentUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

/** The hosted consent picker URL with a return hop back to the Usage tab. The
 *  backend's `consent_url` normally carries a query string already (contract:
 *  Task 5 / watchtower-api `env.consentUrl`), but the joiner is derived rather
 *  than assumed so a query-less value still produces a valid URL. */
export function consentRedirectUrl(consentUrl: string): string {
  const back = window.location.origin + CONSENT_RETURN_PATH;
  const joiner = consentUrl.includes('?') ? '&' : '?';
  return `${consentUrl}${joiner}redirect_uri=${encodeURIComponent(back)}`;
}

/** Boot hop for the consent return: translate `?wt=<tab>` into the `#<tab>` the
 *  SPA actually routes on, then strip the param so a reload doesn't re-apply it.
 *  No-op when the param is absent or isn't a plausible tab id.
 *
 *  Must run BEFORE React mounts (see main.tsx): AuthProvider's boot effect calls
 *  `history.replaceState(pathname + hash)`, which would drop the query, and a
 *  parent's effect runs after its children's — an App-level effect would be too
 *  late. */
export function applyConsentReturn(): void {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get(CONSENT_RETURN_PARAM);
  // Whitelist the shape rather than trusting the value: this lands in the URL
  // fragment, so anything exotic has no business here. An unknown-but-plausible
  // id is harmless — tabFromHash falls back to Home.
  if (!tab || !/^[a-z]{1,20}$/.test(tab)) return;
  params.delete(CONSENT_RETURN_PARAM);
  const query = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}#${tab}`,
  );
}
